import React from 'react';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { head, pickBy, toUpper, find, get } from 'lodash';
import { sortDates, formatDate } from './dateUtils';
import { formatAmount } from './numberUtils';
import { formatAccountDetails } from './accountUtils';
import { whoPaysMapping, BACS_DEBIT_CODE } from '../config';
import { SOFT, PENDING, CONST_INSTRUCTED, CONST_EQUIVALENT, HARD, UPLOAD } from '../constants/Constants';

export const sortOtherPaymentsDateComparison = (isDirectionAscending) => {
	if (isDirectionAscending) {
		return (a, b) => sortDates(a.start_date, b.start_date);
	}
	return (a, b) => sortDates(b.start_date, a.start_date);
};

export const sortMyPaymentsDateComparison = (isDirectionAscending) => {
	if (isDirectionAscending) {
		return (a, b) => sortDates(head(a.payments).start_date, head(b.payments).start_date);
	}
	return (a, b) => sortDates(head(b.payments).start_date, head(a.payments).start_date);
};

export const sortByDate =
	(sortField = 'date') =>
	(sortDirection) => {
		if (sortDirection === 'asc') {
			return (a, b) => sortDates(a[sortField], b[sortField]);
		}
		return (a, b) => sortDates(b[sortField], a[sortField]);
	};

const sortString = (a, b) => {
	if (a > b) return 1;
	if (a < b) return -1;
	return 0;
};

export const sortByString = (sortField) => (sortDirection) => {
	if (sortDirection === 'asc') {
		return (a, b) => sortString(a[sortField], b[sortField]);
	}
	return (a, b) => sortString(b[sortField], a[sortField]);
};

const sortNumber = (a, b) => {
	const aNum = Number(a);
	const bNum = Number(b);

	if (aNum > bNum) return 1;
	if (aNum < bNum) return -1;
	return 0;
};

export const sortByAmount =
	(sortField = 'amountSort') =>
	(sortDirection) => {
		if (sortDirection === 'asc') {
			return (a, b) => sortNumber(a[sortField], b[sortField]);
		}
		return (a, b) => sortNumber(b[sortField], a[sortField]);
	};

export const getPaymentStopOption = (payment) => {
	if (!payment.frequency) {
		return null;
	}
	if (payment.end_date) {
		return 'on-date';
	}

	return 'on-cancel';
};

export const getChapsPaymentDetails = (content, verificationInfo, paymentInfo, fxCharge) => {
	const {
		typeLabel,
		fromLabel,
		paymentDateLabel,
		createdByLabel,
		createdOnLabel,
		toLabel,
		amountLabel,
		refLabel,
		feesLabel,
		authorisedBy,
	} = content;
	const { created_timestamp, type } = verificationInfo;
	const { from_account, submission_date, to_account, amount, creator_name, reference, approvers, start_date } =
		paymentInfo;
	const chargeAmount = get(fxCharge, 'value');
	return pickBy({
		[typeLabel]: toUpper(type),
		[fromLabel]: formatAccountDetails(from_account),
		[toLabel]: formatAccountDetails(to_account),
		[amountLabel]: formatAmount(amount),
		[refLabel]: reference,
		[feesLabel]: chargeAmount ? formatAmount(chargeAmount) : '-',
		[paymentDateLabel]: formatDate(start_date || submission_date),
		[createdByLabel]: creator_name,
		[createdOnLabel]: formatDate(created_timestamp),
		[authorisedBy]: get(find(approvers, { status: 'VERIFIED' }), 'approver', ''),
	});
};

export const getChapsBatchPaymentDetails = (content, verificationInfo, paymentInfo) => {
	const { typeLabel, fromLabel, paymentDateLabel, createdByLabel, createdOnLabel, totalAmountLabel, transactions } =
		content;
	const { created_timestamp, type, total } = verificationInfo;
	const { from_account, submission_date, length, creator_name } = paymentInfo;
	return pickBy({
		[typeLabel]: toUpper(type),
		[fromLabel]: formatAccountDetails(from_account),
		[paymentDateLabel]: formatDate(submission_date),
		[totalAmountLabel]: formatAmount(total),
		[transactions]: length,
		[createdByLabel]: creator_name,
		[createdOnLabel]: formatDate(created_timestamp),
	});
};

export const getBatchSinglePaymentInfo = (content, paymentDetails, fxCharge) => {
	const { fromLabel, toLabel, amountLabel, refLabel, feesLabel } = content;
	const { from_account, to_account, amount, reference } = paymentDetails;
	const chargeAmount = get(fxCharge, 'value');
	return pickBy({
		[fromLabel]: formatAccountDetails(from_account),
		[toLabel]: formatAccountDetails(to_account),
		[amountLabel]: formatAmount(amount),
		[refLabel]: reference,
		[feesLabel]: chargeAmount ? formatAmount(chargeAmount) : '-',
	});
};

export const getPaymentFeesTitle = (fees, chargingModal) => {
	const getChargingModalContent = get(
		find(whoPaysMapping, (whoPays) => whoPays.id === chargingModal),
		'type'
	);
	return `${fees} (${getChargingModalContent})`;
};
export const getDebitOrCreditAmount = (paymentAmount = [], currency) =>
	paymentAmount.find(
		(amount = {}) => amount.currency === currency && [CONST_INSTRUCTED, CONST_EQUIVALENT].includes(amount.type)
	);

export const showAuthoriseLink = (payment) => {
	const { paymentAuthType, file_upload, status } = payment;
	return paymentAuthType === SOFT && file_upload && status === PENDING;
};

export const showAccountDetails = (accDetails, index) => ({
	rowId: index,
	label: formatAccountDetails(accDetails),
});

export const showAmount = (amount, index) => ({
	rowId: index,
	label: formatAmount(amount),
});

export const getFormattedReference = (reference, index) => ({
	rowId: index,
	label: reference,
});

export const showHmrcReference = (hmrcReference, index) => ({
	rowId: index,
	label: hmrcReference,
});

export const showTransactionCode = (code, index) => ({
	rowId: index,
	label: code,
});

export const getMyDataForFileUpload = (payments) =>
	payments.map((payment, index) => {
		const { bacsType, transaction_code, hmrc_reference, to_account, amount, reference, from_account } = payment;
		const getCodeOrRef =
			bacsType === BACS_DEBIT_CODE
				? showTransactionCode(transaction_code, index)
				: showHmrcReference(hmrc_reference, index);
		const accountDetails = bacsType === BACS_DEBIT_CODE ? from_account : to_account;
		return [
			showAccountDetails(accountDetails, index),
			showAmount(amount, index),
			getFormattedReference(reference, index),
			getCodeOrRef,
		];
	});

export const showReviewFooterButton = (payments) => payments && payments.batch_type === UPLOAD;

export const fileUploadViewSoftToken = (fileUploadPaymentByOthers, toAuthorise) =>
	fileUploadPaymentByOthers && toAuthorise.verification_type !== HARD;

export const showFeesLabel = (chargeAmount, index) => ({
	rowId: index,
	label: chargeAmount || '-',
});

export const getChapsFileUploadData = (payments, fxCharge) => {
	const chargeAmount = get(fxCharge, 'value');
	return payments.map((payment, index) => [
		showAccountDetails(payment.from_account, index),
		showAccountDetails(payment.to_account, index),
		showAmount(payment.amount, index),
		getFormattedReference(payment.reference, index),
		showFeesLabel(chargeAmount, index),
	]);
};

export const isFileUploadPayment = (batchPayment) => {
	const { payments, bacs, chaps } = batchPayment;
	return get(bacs || head(payments || chaps), 'file_upload');
};
